"use client";

import { Box, Button, Container, Typography } from "@mui/material";
import { useTranslations } from "next-intl";
import { Link } from "@/i18n/routing";
import PricingCard from "@/app/components/PricingCard";
import { useTheme } from "@mui/material/styles";
import { ThemeProvider } from "@mui/material/styles";
import { darkTheme, lightTheme } from "../theme/theme";

/**
 * 首页会员部分组件
 *
 * 基于UI设计实现的会员计划展示组件，包含：
 * - "Pick Your Path, Scale Your Capital" 标题
 * - 描述文案和Get Your Edge按钮
 * - 三个定价卡片（Explorer、Visionary、Prime Pass）
 * - 底部免责声明
 * - 响应式布局和主题适配
 */
export default function HomeMembershipSection() {
  const theme = useTheme();
  return (
    <Box>
      <ThemeProvider theme={theme.palette.mode === "dark" ? lightTheme : darkTheme}>
        <HomeMembershipSectionInner />
      </ThemeProvider>
    </Box>
  );
}
export function HomeMembershipSectionInner() {
  const t = useTranslations("Home.membership");
  const membershipT = useTranslations("Membership") as any;
  const theme = useTheme();

  // 获取功能特性列表
  const ExplorerFeatures: string[] = membershipT.raw("pricing.explorer.features");
  const VisionaryFeatures: string[] = membershipT.raw("pricing.visionary.features");
  const PrimeFeatures: string[] = membershipT.raw("pricing.prime.features");

  // 独立的外侧阴影效果对象 - 可复用
  const getCardOuterShadow = (top = "2.5rem", left = "2px", right = "2px", bottom = "0px") => ({
    content: '""',
    position: "absolute" as const,
    top,
    left,
    right,
    bottom,
    background: "transparent",
    boxShadow: theme.palette.mode === "dark"
      ? "0 15px 45px rgba(255, 255, 255, 0.05), 0 8px 20px rgba(255, 255, 255, 0.05)"
      : "0 15px 45px rgba(0, 0, 0, 0.15), 0 8px 20px rgba(0, 0, 0, 0.1)",
    borderRadius: "1rem",
    pointerEvents: "none" as const,
    zIndex: -1,
  });

  return (
    <Box
      sx={{
        bgcolor: "background.default",
        borderTopLeftRadius: "1.5rem",
        borderTopRightRadius: "1.5rem",
        mt: "-1.5rem",
        zIndex: 1,
        position: "relative",
        py: { xs: 6, md: 10 },
      }}>
      <Container maxWidth="lg">
        {/* 标题部分 */}
        <Box sx={{ textAlign: "center" }}>
          <Typography
            variant="h2"
            sx={{
              fontSize: "var(--font-size-3xl)",
              fontWeight: "600",
              lineHeight: 1.3,
              color: "text.primary",
            }}>
            {t("title")}
          </Typography>

          {/* 副标题文案 */}
          <Typography
            variant="body1"
            sx={{
              lineHeight: 1.6,
              color: "text.primary",
              maxWidth: "50rem",
              mx: "auto",
              mt: "1.5rem",
            }}>
            {t.rich("description", {
              br: () => <br />
            })}
          </Typography>

          {/* Get Your Edge 按钮 */}
          <Link href="/membership">
            <Button
              variant="contained"
              color="tertiary"
              size="large"
              sx={{
                mt: "2.5rem",
                fontWeight: "600",
                "&:hover": {
                  opacity: 0.9,
                },
              }}>
              {t("button")}
            </Button>
          </Link>
        </Box>

        {/* 定价卡片区域 */}
        <Box
          sx={{
            mt: "4.19rem",
            height: "39rem",
            overflow: "hidden", // 完全隐藏滚动条
            display: "flex",
            justifyContent: {xs: "space-between", md: "center"}, // 手机端使用space-between，桌面端使用center
            position: "relative",
            // 整体底部渐变遮罩 - 从底部深色到上面透明
            "&::after": {
              content: '""',
              position: "absolute",
              bottom: "0",
              left: 0,
              right: 0,
              height: "2.5rem",
              background:
                theme.palette.mode === "dark"
                  ? `linear-gradient(to top, ${theme.palette.background.default} 0%, transparent 100%)`
                  : `linear-gradient(to top, ${theme.palette.background.default} 0%, transparent 100%)`,
              pointerEvents: "none",
              zIndex: 50, // 确保在所有卡片之上
            },
            // 当没有任何卡片被hover时，为中间卡片显示阴影
            ".explorer-card::before": getCardOuterShadow(),
          }}>
          {/* Visionary 计划 - 左侧 */}
          <Box
            className="visionary-card"
            sx={{
              width: "24rem",
              height: "39.625rem",
              display: "flex",
              alignItems: "stretch",
              position: "relative",
              zIndex: { xs: "auto", lg: 1 },
              mt: "2.31rem",
              mr: { xs: "-65vw", sm: "-8rem", md: "-4rem" }, // 桌面端与中间卡片重叠
              overflow: "hidden", // 隐藏超出部分
            }}>
            <PricingCard
              title={membershipT("pricing.visionary.title")}
              subtitle={membershipT("pricing.visionary.subtitle")}
              price={membershipT("pricing.visionary.price")}
              period={membershipT("pricing.visionary.period")}
              features={VisionaryFeatures}
              label={membershipT("pricing.visionary.label")}
              labelColor="#139E79"
              labelBgColor="rgba(19, 158, 121, 0.30)"
              hasHover={false}
            />
          </Box>

          {/* Explorer 计划 - 中间，略高，前景，带外侧阴影 */}
          <Box
            className="explorer-card"
            sx={{
              width: "24rem",
              height: "41.8375rem",
              display: "flex",
              mt: "0.2rem", // 防止scale(1.01)出界被隐藏
              alignItems: "stretch",
              position: "relative",
              zIndex: 3, // 最高层级
            }}>
            <PricingCard
              title={membershipT("pricing.explorer.title")}
              subtitle={membershipT("pricing.explorer.subtitle")}
              price={membershipT("pricing.explorer.price")}
              period={membershipT("pricing.explorer.period")}
              features={ExplorerFeatures}
              label={membershipT("pricing.explorer.label")}
              labelColor="#E05357"
              labelBgColor="rgba(224, 83, 87, 0.30)"
              hasHover={false}
            />
          </Box>

          {/* Prime Pass 计划 - 右侧 */}
          <Box
            sx={{
              width: "24rem",
              height: "39.625rem",
              display: "flex",
              alignItems: "stretch",
              position: "relative",
              zIndex: { xs: "auto", lg: 1 },
              mt: "2.51rem",
              ml: { xs: "-65vw", sm: "-8rem", md: "-1rem" }, // 桌面端与中间卡片重叠
              overflow: "hidden", // 隐藏超出部分
            }}>
            <PricingCard
              title={membershipT("pricing.prime.title")}
              subtitle={membershipT("pricing.prime.subtitle")}
              price={membershipT("pricing.prime.price")}
              period=""
              features={PrimeFeatures}
              label={membershipT("pricing.prime.label")}
              labelColor="#CC9E5A"
              labelBgColor="rgba(204, 158, 90, 0.30)"
              hasHover={false}
            />
          </Box>
        </Box>
        {/* 底部免责声明 */}
        <Box sx={{ textAlign: "center" }}>
          <Typography
            variant="body2"
            sx={{
              fontSize: "0.875rem",
              lineHeight: 1.6,
              color: "text.tertiary",
              maxWidth: "800px",
              mx: "auto",
              mt: "1rem",
            }}>
            {t("disclaimer")}
          </Typography>
        </Box>
      </Container>
    </Box>
  );
}
